import React, { useState, useEffect, useMemo } from 'react'
import {
  Loader2, AlertCircle, Search, ChevronDown, ChevronUp,
  BookOpen, ExternalLink, Download, FileText, Video, Link as LinkIcon,
  Pin, Eye, Inbox,
} from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import supportService, {
  type TrainingMaterial,
  TRAINING_CATEGORIES,
} from '@/services/api/supportService'
import { apiClient } from '@/services/apiClient'

/**
 * Convert a video URL (YouTube, MS Stream, Vimeo, SharePoint) to an embeddable iframe URL.
 */
function getEmbedUrl(url: string): string | null {
  try {
    const u = new URL(url)

    // YouTube
    if (u.hostname.includes('youtube.com') || u.hostname.includes('youtu.be')) {
      const videoId = u.hostname.includes('youtu.be')
        ? u.pathname.slice(1)
        : u.searchParams.get('v')
      if (videoId) return `https://www.youtube.com/embed/${videoId}`
    }

    // Vimeo
    if (u.hostname.includes('vimeo.com')) {
      const match = u.pathname.match(/\/(\d+)/)
      if (match) return `https://player.vimeo.com/video/${match[1]}`
    }

    // MS Stream (new) — web.microsoftstream.com
    if (u.hostname.includes('microsoftstream.com')) {
      return url
    }

    // SharePoint / OneDrive — only embed.aspx URLs can be iframed;
    // sharing URLs (/:v:/g/...) set frame-ancestors 'none' and will be blocked.
    if (u.hostname.includes('sharepoint.com') || u.hostname.includes('onedrive.com')) {
      if (u.pathname.includes('embed.aspx')) {
        return url
      }
      // Sharing URL — cannot be embedded, fall back to external link
      return null
    }

    return null
  } catch {
    return null
  }
}

function getCategoryLabel(value: string): string {
  return TRAINING_CATEGORIES.find(c => c.value === value)?.label || value.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase())
}

function getContentTypeIcon(type: string) {
  switch (type) {
    case 'article': return <BookOpen className="w-3.5 h-3.5" />
    case 'video': return <Video className="w-3.5 h-3.5" />
    case 'file': return <FileText className="w-3.5 h-3.5" />
    case 'link': return <LinkIcon className="w-3.5 h-3.5" />
    default: return <BookOpen className="w-3.5 h-3.5" />
  }
}

function formatFileSize(bytes?: number): string {
  if (!bytes) return ''
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

const TrainingMaterials: React.FC = () => {
  const [materials, setMaterials] = useState<TrainingMaterial[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [search, setSearch] = useState('')
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null)
  const [expandedId, setExpandedId] = useState<number | null>(null)
  const [videoUrls, setVideoUrls] = useState<Record<number, string>>({})
  const [captionUrls, setCaptionUrls] = useState<Record<number, string>>({})
  const [downloadingFile, setDownloadingFile] = useState<string | null>(null)

  const fetchVideoUrl = async (materialId: number, materialPublicId: string) => {
    if (videoUrls[materialId]) return
    try {
      const result = await supportService.getTrainingVideoUrl(materialPublicId)
      let url = result.video_url

      if (url.startsWith('/')) {
        // Relative path = local dev fallback (no SAS URL available).
        // Fetch through the authenticated API client and create a blob URL,
        // since <video src> can't send auth headers.
        const response = await apiClient.get(url, { responseType: 'blob' })
        url = URL.createObjectURL(response.data as Blob)
      }

      setVideoUrls(prev => ({ ...prev, [materialId]: url }))
    } catch {
      // Silently fail — video won't render
    }
  }

  const fetchCaptionUrl = async (materialId: number, materialPublicId: string) => {
    if (captionUrls[materialId]) return
    try {
      const result = await supportService.getTrainingCaptionUrl(materialPublicId)
      let url = result.caption_url

      if (url.startsWith('/')) {
        // Local dev fallback — fetch the .vtt through the authenticated API
        // client and create a blob URL, since <track src> (like <video src>)
        // can't send the Authorization header.
        const response = await apiClient.get(url, { responseType: 'blob' })
        // Force the WebVTT MIME type so the browser parses it as captions.
        const vttBlob = new Blob([response.data as Blob], { type: 'text/vtt' })
        url = URL.createObjectURL(vttBlob)
      }

      setCaptionUrls(prev => ({ ...prev, [materialId]: url }))
    } catch {
      // Silently fail — captions just won't be available
    }
  }

  const handleFileDownload = async (material: TrainingMaterial) => {
    if (downloadingFile) return
    setDownloadingFile(material.material_id)
    try {
      const result = await supportService.getTrainingFileUrl(material.material_id)
      const url = result.file_url

      if (url.startsWith('/')) {
        // Local dev fallback — fetch through authenticated API client
        const response = await apiClient.get(url, { responseType: 'blob' })
        const blobUrl = URL.createObjectURL(response.data as Blob)
        const a = document.createElement('a')
        a.href = blobUrl
        a.download = result.filename || material.file_filename || 'download'
        document.body.appendChild(a)
        a.click()
        document.body.removeChild(a)
        URL.revokeObjectURL(blobUrl)
      } else {
        // SAS URL — open directly
        window.open(url, '_blank', 'noopener,noreferrer')
      }
    } catch {
      // Silently fail
    } finally {
      setDownloadingFile(null)
    }
  }

  const loadMaterials = async () => {
    setLoading(true)
    setError(null)
    try {
      const params: Record<string, string> = {}
      if (selectedCategory) params.category = selectedCategory
      if (search.trim()) params.search = search.trim()
      const result = await supportService.getPublishedTraining(params)
      setMaterials(result.materials)
    } catch {
      setError('Failed to load training materials')
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    loadMaterials()
  }, [selectedCategory])

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      loadMaterials()
    }, 300)
    return () => clearTimeout(timer)
  }, [search])

  // Get unique categories from loaded materials for filter pills
  const availableCategories = useMemo(() => {
    return TRAINING_CATEGORIES.filter(cat =>
      materials.some(m => m.category === cat.value) || selectedCategory === cat.value
    )
  }, [materials, selectedCategory])

  const toggleExpand = (id: number) => {
    const newExpandedId = expandedId === id ? null : id
    setExpandedId(newExpandedId)
    if (newExpandedId !== null) {
      const material = materials.find(m => m.id === newExpandedId)
      if (material) {
        // Track view by fetching individual material (increments view_count server-side)
        supportService.getTrainingMaterial(material.material_id).then(updated => {
          setMaterials(prev => prev.map(m => m.id === updated.id ? { ...m, view_count: updated.view_count } : m))
        }).catch(() => {})
        if (material.video_blob_path) {
          fetchVideoUrl(material.id, material.material_id)
          if (material.caption_blob_path) {
            fetchCaptionUrl(material.id, material.material_id)
          }
        }
      }
    }
  }

  if (loading && materials.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-6 h-6 animate-spin text-heritage-blue" />
      </div>
    )
  }

  return (
    <div className="flex flex-col h-full">
      {/* Search */}
      <div className="px-4 pt-4 pb-2">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search training materials..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-heritage-blue"
          />
        </div>
      </div>

      {/* Category filter pills */}
      <div className="px-4 pb-3 flex gap-1.5 overflow-x-auto no-scrollbar">
        <button
          onClick={() => setSelectedCategory(null)}
          className={`flex-shrink-0 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
            !selectedCategory
              ? 'bg-heritage-blue text-white border-heritage-blue'
              : 'text-gray-600 border-gray-200 hover:border-gray-400'
          }`}
        >
          All
        </button>
        {TRAINING_CATEGORIES.map(cat => (
          <button
            key={cat.value}
            onClick={() => setSelectedCategory(selectedCategory === cat.value ? null : cat.value)}
            className={`flex-shrink-0 px-3 py-1 text-xs font-medium rounded-full border transition-colors ${
              selectedCategory === cat.value
                ? 'bg-heritage-blue text-white border-heritage-blue'
                : 'text-gray-600 border-gray-200 hover:border-gray-400'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* Error */}
      {error && (
        <div className="mx-4 mb-3 p-3 bg-red-50 text-red-700 rounded-lg flex items-center gap-2 text-sm">
          <AlertCircle className="w-4 h-4 flex-shrink-0" />
          {error}
        </div>
      )}

      {/* Materials list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4 space-y-2">
        {materials.length === 0 && !loading ? (
          <div className="flex flex-col items-center justify-center py-12 text-gray-400">
            <Inbox className="w-10 h-10 mb-3" />
            <p className="text-sm font-medium">No training materials found</p>
            <p className="text-xs mt-1">
              {search || selectedCategory ? 'Try adjusting your filters' : 'Check back later for new content'}
            </p>
          </div>
        ) : (
          materials.map(material => {
            const isExpanded = expandedId === material.id
            const embedUrl = material.video_url ? getEmbedUrl(material.video_url) : null

            return (
              <div
                key={material.id}
                className="border rounded-lg overflow-hidden bg-white hover:shadow-sm transition-shadow"
              >
                {/* Header */}
                <button
                  onClick={() => toggleExpand(material.id)}
                  className="w-full text-left px-4 py-3 flex items-start gap-3"
                >
                  <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-lg bg-heritage-blue/10 flex items-center justify-center text-heritage-blue">
                    {getContentTypeIcon(material.content_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      {material.is_pinned && <Pin className="w-3 h-3 text-amber-500 flex-shrink-0" />}
                      <h4 className="text-sm font-semibold text-gray-900 truncate">{material.title}</h4>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge variant="outline" className="text-[10px] px-1.5 py-0">
                        {getCategoryLabel(material.category)}
                      </Badge>
                      <span className="text-[10px] text-gray-400 flex items-center gap-1">
                        <Eye className="w-3 h-3" /> {material.view_count}
                      </span>
                    </div>
                    {material.description && !isExpanded && (
                      <p className="text-xs text-gray-500 mt-1 line-clamp-2">{material.description}</p>
                    )}
                  </div>
                  <div className="flex-shrink-0 text-gray-400">
                    {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                  </div>
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="px-4 pb-4 border-t bg-gray-50/50">
                    {/* Description */}
                    {material.description && (
                      <p className="text-sm text-gray-600 mt-3 mb-3">{material.description}</p>
                    )}

                    {/* Uploaded video (self-hosted via SAS URL) */}
                    {material.video_blob_path && (
                      <div className="mb-3 aspect-video rounded-lg overflow-hidden bg-black">
                        {videoUrls[material.id] ? (
                          <video
                            src={videoUrls[material.id]}
                            controls
                            className="w-full h-full"
                            preload="metadata"
                            title={material.title}
                            crossOrigin="anonymous"
                          >
                            {material.caption_blob_path && captionUrls[material.id] && (
                              <track
                                kind="subtitles"
                                src={captionUrls[material.id]}
                                srcLang="en"
                                label="English"
                                default
                              />
                            )}
                            Your browser does not support the video element.
                          </video>
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Loader2 className="w-6 h-6 animate-spin text-white" />
                          </div>
                        )}
                      </div>
                    )}

                    {/* External video embed (YouTube, Vimeo) — only if no uploaded video */}
                    {!material.video_blob_path && embedUrl && (
                      <div className="mb-3 aspect-video rounded-lg overflow-hidden bg-black">
                        <iframe
                          src={embedUrl}
                          className="w-full h-full"
                          allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                          allowFullScreen
                          title={material.title}
                        />
                      </div>
                    )}
                    {!material.video_blob_path && embedUrl && material.video_url && (
                      <p className="text-xs text-gray-400 mb-2">
                        Video not loading?{' '}
                        <a href={material.video_url} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline">
                          Open in new tab
                        </a>
                      </p>
                    )}

                    {/* Rich text content */}
                    {material.content_html && (
                      <div
                        className="prose prose-sm max-w-none mt-3 text-gray-700"
                        dangerouslySetInnerHTML={{ __html: material.content_html }}
                      />
                    )}

                    {/* Actions */}
                    <div className="flex items-center gap-2 mt-4 flex-wrap">
                      {material.external_url && (
                        <a
                          href={material.external_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-heritage-blue bg-heritage-blue/10 rounded-lg hover:bg-heritage-blue/20 transition-colors"
                        >
                          <ExternalLink className="w-3.5 h-3.5" />
                          Open Link
                        </a>
                      )}
                      {material.video_blob_path && material.video_blob_filename && (
                        <span className="text-[10px] text-gray-400">
                          {material.video_blob_filename}
                          {material.video_blob_size_bytes ? ` (${formatFileSize(material.video_blob_size_bytes)})` : ''}
                        </span>
                      )}
                      {!material.video_blob_path && material.video_url && !embedUrl && (
                        <a
                          href={material.video_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-purple-700 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors"
                        >
                          <Video className="w-3.5 h-3.5" />
                          Watch Video
                        </a>
                      )}
                      {material.file_url && (
                        <button
                          onClick={() => handleFileDownload(material)}
                          disabled={downloadingFile === material.material_id}
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-green-700 bg-green-50 rounded-lg hover:bg-green-100 transition-colors disabled:opacity-50"
                        >
                          {downloadingFile === material.material_id ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Download className="w-3.5 h-3.5" />
                          )}
                          {material.file_filename || 'Download'}
                          {material.file_size_bytes ? ` (${formatFileSize(material.file_size_bytes)})` : ''}
                        </button>
                      )}
                    </div>

                    {/* Meta */}
                    <p className="text-[10px] text-gray-400 mt-3">
                      By {material.created_by_name} · {new Date(material.created_at).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' })}
                    </p>
                  </div>
                )}
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}

export default TrainingMaterials
